import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';

const LineChart = ({ data = [], labelKey, valueKey, height = 260, seriesName = 'Value', color = '#5E35B1' }) => {
  const chartConfig = useMemo(() => {
    const categories = data.map(item => item?.[labelKey] ?? '');
    const seriesData = data.map(item => Number(item?.[valueKey] ?? 0));

    return {
      options: {
        chart: {
          type: 'area',
          toolbar: { show: false },
          zoom: { enabled: false },
          sparkline: { enabled: false },
        },
        stroke: {
          curve: 'smooth',
          width: 3,
        },
        dataLabels: {
          enabled: true,
          formatter: val => (Number.isFinite(val) ? Math.round(val) : val),
          offsetY: -10,
          style: {
            fontSize: '11px',
            fontWeight: 600,
          },
        },
        markers: {
          size: 5,
          strokeWidth: 2,
          colors: ['#FFFFFF'],
          strokeColors: color,
          hover: { size: 7 },
        },
        xaxis: {
          categories,
          labels: { rotate: -45, style: { fontSize: '11px' } },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            formatter: val => (Number.isFinite(val) ? Math.round(val) : val),
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.28,
            opacityTo: 0.02,
            stops: [25, 60, 100],
            colorStops: [
              { offset: 0, color, opacity: 0.3 },
              { offset: 60, color, opacity: 0.12 },
              { offset: 100, color, opacity: 0 },
            ],
          },
        },
        tooltip: {
          y: {
            formatter: val => (Number.isFinite(val) ? val.toLocaleString() : val),
          },
        },
        colors: [color],
        grid: {
          borderColor: 'rgba(148, 163, 184, 0.2)',
          strokeDashArray: 4,
        },
      },
      series: [
        {
          name: seriesName,
          data: seriesData,
        },
      ],
    };
  }, [data, labelKey, valueKey, color, seriesName]);

  if (!data?.length) {
    return (
      <div style={{ height }} className="flex items-center justify-center text-sm text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <ReactApexChart
      options={chartConfig.options}
      series={chartConfig.series}
      type="area"
      height={height}
    />
  );
};

LineChart.propTypes = {
  data: PropTypes.array,
  labelKey: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  height: PropTypes.number,
  seriesName: PropTypes.string,
  color: PropTypes.string,
};

export default LineChart;